import { TFunction } from 'react-i18next'

import { DensityChange } from '../../../../api/blueprintFamilies/trendsBatch'
import { densityChangeDelta, formatDensity, formatDensityDelta } from './catalogueUtils'

// The density counterpart of `adoptionChangeHover`: the hover of the ranked list's
// Change column on the density lens. A one-line definition, then a paragraph that names
// the first and last month, the two values and the signed difference between them. When the
// cell shows "—" because a month had no adopting games, the paragraph says which month.
//
// Takes `t` rather than calling the hook so it stays usable from memoized render paths.
// The paragraphs are separated by a blank line; GRTooltip renders with `white-space: pre-line`.

export interface DensityChangeCopyArgs {
  // Density of the period's first and last month. Null/undefined = unknown.
  change: DensityChange | null | undefined
  // The month the change is measured from (`changeBaselineLabel`).
  baselineLabel?: string
  // The period's last month (`changeLatestLabel`).
  latestLabel?: string
}

const PARAGRAPH_BREAK = '\n\n'

// The backend reports exactly 0 for a month with no adopting games (apiserver #328), so a
// 0 end is the one case where "no games ran these events" is true. Any other reason for
// "—" (no series for the month, a non-finite value) gets no explanation rather than a wrong one.
function notShownReason(change: DensityChange, baselineLabel: string, latestLabel: string, t: TFunction): string | undefined {
  const firstEmpty = change.first === 0
  const lastEmpty = change.last === 0
  if (firstEmpty && lastEmpty) {
    return t('market-analytics:catalogue_density_change_not_shown_both_months', { firstMonth: baselineLabel, lastMonth: latestLabel })
  }
  if (firstEmpty) return t('market-analytics:catalogue_density_change_not_shown_month', { month: baselineLabel })
  if (lastEmpty) return t('market-analytics:catalogue_density_change_not_shown_month', { month: latestLabel })
  return undefined
}

export function densityChangeHover({ change, baselineLabel, latestLabel }: DensityChangeCopyArgs, t: TFunction): string {
  const definition = t('market-analytics:tooltip_density_change')
  if (!change || !baselineLabel || !latestLabel) return definition

  const delta = densityChangeDelta(change)
  if (delta === null) {
    const reason = notShownReason(change, baselineLabel, latestLabel, t)
    return reason ? `${definition}${PARAGRAPH_BREAK}${reason}` : definition
  }

  const key = delta > 0 ? 'catalogue_density_change_more' : delta < 0 ? 'catalogue_density_change_fewer' : 'catalogue_density_change_same'
  const sentence = t(`market-analytics:${key}`, {
    firstMonth: baselineLabel,
    lastMonth: latestLabel,
    firstValue: formatDensity(change.first),
    lastValue: formatDensity(change.last),
    delta: formatDensityDelta(delta),
  })
  return `${definition}${PARAGRAPH_BREAK}${sentence}`
}
